import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Clock, Crown, Heart, Image as ImageIcon } from "lucide-react";
import { API } from "@/lib/api";
import { SUPPORT_EMAIL } from "@/lib/pricing";

const FACTS = [
  { icon: BookOpen, title: "KDP-ready print PDFs", desc: "Trim sizes, gutters, bleed and a full cover wrap, checked against Amazon's rules before you upload." },
  { icon: ImageIcon, title: "Covers checked at thumbnail size", desc: "Every title is typeset in real fonts and tested at Amazon's 100px thumbnail so it reads in search results." },
  { icon: Clock, title: "Preview free, pay per book", desc: "See the cover, contents and first page free. Finish a book for $45, or as little as $35 each when you buy 18+. No subscription, KDP files included." },
  { icon: Heart, title: "Your words, your rights", desc: "You own what you publish. Your author bio is printed exactly as you write it, and we never invent it." },
];

const Fact = ({ f, i }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
    transition={{ duration: 0.6, delay: i * 0.08 }} className="flex items-start gap-3" data-testid={`fact-${i}`}>
    <f.icon className="w-6 h-6 text-gold-400 mt-0.5 shrink-0" strokeWidth={1.5} />
    <div>
      <div className="font-ui text-sm font-semibold text-slate-100">{f.title}</div>
      <p className="font-read text-sm text-slate-400 mt-1 leading-relaxed">{f.desc}</p>
    </div>
  </motion.div>
);

export const FoundingOffer = ({ onGetStarted }) => {
  const [offer, setOffer] = useState(null);
  useEffect(() => { axios.get(`${API}/payments/founding`).then((r) => setOffer(r.data)).catch(() => {}); }, []);
  const open = offer && offer.remaining > 0;
  return (
    <section className="bg-[#080E21] border-y border-gold-500/10" data-testid="founding-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-center">
        <div className="rounded-2xl border border-gold-400/30 bg-gold-400/[0.04] p-7" data-testid="founding-offer">
          <div className="flex items-center gap-2 font-ui text-xs uppercase tracking-[0.3em] text-gold-400 font-semibold mb-3">
            <Crown className="w-4 h-4" /> Founding Authors
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-50 leading-tight">
            {open ? "Be one of our first 10 authors." : "Our founding author spots are taken."}
          </h2>
          <p className="font-read text-slate-300 mt-3 leading-relaxed">
            {open
              ? "We're just opening the studio. The first 10 customers get a bonus book credit, added automatically with their first purchase."
              : "Thank you to our first 10 authors. Every book still starts with a free preview."}
          </p>
          {open && (
            <p data-testid="founding-terms" className="font-ui text-xs text-slate-500 mt-2">
              One bonus per customer (checked by payment card and checkout email). If you share a card and think you were wrongly excluded, email {SUPPORT_EMAIL}.
            </p>
          )}
          {offer && (
            <div className="mt-5" data-testid="founding-spots">
              <div className="h-1.5 rounded-full bg-navy-900 overflow-hidden">
                <div className="h-full bg-gold-400 transition-[width] duration-700" style={{ width: `${(offer.claimed / offer.limit) * 100}%` }} />
              </div>
              <div className="font-ui text-xs text-slate-400 mt-2" data-testid="founding-remaining">
                {offer.remaining} of {offer.limit} founding spots left
              </div>
            </div>
          )}
          <button data-testid="founding-cta" onClick={onGetStarted}
            className="group gold-pill rounded-full px-6 py-3 mt-6 font-ui text-sm inline-flex items-center gap-2">
            Start with a free preview <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-8">
          {FACTS.map((f, i) => <Fact key={f.title} f={f} i={i} />)}
        </div>
      </div>
    </section>
  );
};
